import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, { Background, Controls, ReactFlowProvider, MarkerType, applyNodeChanges } from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import { Alert, Empty, Space, Typography, message } from "antd";
import { EditOutlined, LockOutlined, SaveOutlined, UndoOutlined, CloseOutlined } from "@ant-design/icons";
import axios from "axios";
import { ButtonItem, SegmentedItem, MensajeError } from "../../../../Components/Items";
import NodoEtapaFlow from "./NodoEtapaFlow";
import EdgeEditable from "./EdgeEditable";
import {
  calcularNiveles,
  calcularPosiciones,
  posicionesAutomaticas,
  flechasGuardadas,
  transicionesConIndice,
  claveFlecha,
} from "./diagramaEtapas.utils";

const nodeTypes = { etapa: NodoEtapaFlow };
const edgeTypes = { etapa: EdgeEditable };

const SEGUNDOS_RENOVAR_BLOQUEO = 60;
const SEGUNDOS_CONSULTAR_BLOQUEO = 30;

const DiagramaEtapasPU = ({ etapas, scope, empresaId, headersToken, puedeEditar, onAbrirEtapa, onGuardado }) => {
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [bloqueoAjeno, setBloqueoAjeno] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [flechas, setFlechas] = useState(new Map());
  const editandoRef = useRef(false);
  const nodesRef = useRef([]);

  const listaEtapas = useMemo(() => etapas || [], [etapas]);
  const nivelPorId = useMemo(() => calcularNiveles(listaEtapas), [listaEtapas]);

  const esRetrocesoDe = useCallback(
    (origenId, destinoId) => (nivelPorId.get(destinoId) ?? 0) <= (nivelPorId.get(origenId) ?? 0),
    [nivelPorId]
  );

  const construirNodos = useCallback(
    (posiciones, formaDe) =>
      listaEtapas.map((etapa) => ({
        id: etapa._id,
        type: "etapa",
        position: posiciones.get(etapa._id),
        data: { etapa, forma: formaDe(etapa) },
      })),
    [listaEtapas]
  );

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    if (editandoRef.current) {
      const previos = new Map(nodesRef.current.map((n) => [n.id, n]));
      const posicionesPrevias = new Map([...previos].map(([id, n]) => [id, n.position]));
      setNodes(
        construirNodos(calcularPosiciones(listaEtapas, nivelPorId, posicionesPrevias), (etapa) =>
          previos.get(etapa._id)?.data.forma || etapa.diagrama?.forma || "rectangulo"
        )
      );
      return;
    }
    setNodes(construirNodos(calcularPosiciones(listaEtapas, nivelPorId), (etapa) => etapa.diagrama?.forma || "rectangulo"));
    setFlechas(flechasGuardadas(listaEtapas));
  }, [listaEtapas, nivelPorId, construirNodos]);

  const flujoId = scope?.flujoId;
  const parametrosScope = useMemo(() => ({ empresaId, flujoId }), [empresaId, flujoId]);

  const llamar = useCallback((ruta, cuerpo = {}) => axios.post(`etapasOrdenProduccionPU/${ruta}`, { ...parametrosScope, ...cuerpo }, { headers: headersToken }), [
    parametrosScope,
    headersToken,
  ]);

  const consultarBloqueo = useCallback(() => {
    llamar("consultarBloqueoDiagrama")
      .then((res) => {
        if (res.data.success === 2) setBloqueoAjeno(res.data.bloqueado ? res.data.usuario : null);
      })
      .catch(() => {});
  }, [llamar]);

  useEffect(() => {
    if (!puedeEditar || editando) return undefined;
    consultarBloqueo();
    const intervalo = setInterval(consultarBloqueo, SEGUNDOS_CONSULTAR_BLOQUEO * 1000);
    return () => clearInterval(intervalo);
  }, [puedeEditar, editando, consultarBloqueo]);

  const salirDeEdicion = useCallback(() => {
    editandoRef.current = false;
    setEditando(false);
    setNodes(construirNodos(calcularPosiciones(listaEtapas, nivelPorId), (etapa) => etapa.diagrama?.forma || "rectangulo"));
    setFlechas(flechasGuardadas(listaEtapas));
  }, [construirNodos, listaEtapas, nivelPorId]);

  useEffect(() => {
    if (!editando) return undefined;
    const intervalo = setInterval(() => {
      llamar("tomarBloqueoDiagrama")
        .then((res) => {
          if (res.data.success !== 2 || res.data.bloqueado) {
            MensajeError({ message: res.data.bloqueado ? `Perdiste la edición: lo está editando ${res.data.usuario}` : "No se pudo renovar la edición del diagrama" });
            salirDeEdicion();
          }
        })
        .catch(() => {});
    }, SEGUNDOS_RENOVAR_BLOQUEO * 1000);
    return () => clearInterval(intervalo);
  }, [editando, llamar, salirDeEdicion]);

  useEffect(
    () => () => {
      if (editandoRef.current) llamar("liberarBloqueoDiagrama").catch(() => {});
    },
    [llamar]
  );

  useEffect(() => {
    editandoRef.current = false;
    setEditando(false);
  }, [llamar]);

  const empezarEdicion = () => {
    llamar("tomarBloqueoDiagrama")
      .then((res) => {
        if (res.data.success !== 2) return MensajeError({ message: res.data.error || "No se pudo editar el diagrama" });
        if (res.data.bloqueado) {
          setBloqueoAjeno(res.data.usuario);
          return MensajeError({ message: `Lo está editando ${res.data.usuario}` });
        }
        setBloqueoAjeno(null);
        editandoRef.current = true;
        setEditando(true);
      })
      .catch(() => MensajeError({ message: "Error al conectar con el servidor" }));
  };

  const cancelar = () => {
    llamar("liberarBloqueoDiagrama").catch(() => {});
    salirDeEdicion();
  };

  const restablecer = () => {
    setNodes(construirNodos(posicionesAutomaticas(listaEtapas, nivelPorId), () => "rectangulo"));
    setFlechas(new Map());
  };

  const guardar = () => {
    const clavesVigentes = new Set();
    const flechasPorOrigen = new Map();
    listaEtapas.forEach((etapa) => {
      transicionesConIndice(etapa).forEach(({ destinoId, indice }) => {
        const clave = claveFlecha(etapa._id, destinoId, indice);
        clavesVigentes.add(clave);
        const flecha = flechas.get(clave);
        if (!flecha) return;
        if (!flechasPorOrigen.has(etapa._id)) flechasPorOrigen.set(etapa._id, []);
        flechasPorOrigen.get(etapa._id).push({ etapaDestinoId: destinoId, indice, ...flecha });
      });
    });

    const nodos = nodes.map((n) => ({
      etapaId: n.id,
      x: Math.round(n.position.x),
      y: Math.round(n.position.y),
      forma: n.data.forma,
      flechas: flechasPorOrigen.get(n.id) || [],
    }));

    setGuardando(true);
    llamar("guardarDiagrama", { nodos })
      .then((res) => {
        setGuardando(false);
        if (res.data.success !== 2) return MensajeError({ message: res.data.error || "No se pudo guardar el diagrama" });
        message.success("Diagrama guardado");
        llamar("liberarBloqueoDiagrama").catch(() => {});
        editandoRef.current = false;
        setEditando(false);
        if (onGuardado) onGuardado();
      })
      .catch(() => {
        setGuardando(false);
        MensajeError({ message: "Error al conectar con el servidor" });
      });
  };

  const onNodesChange = useCallback((changes) => setNodes((actuales) => applyNodeChanges(changes, actuales)), []);

  const cambiarFlecha = useCallback((clave, patch) => {
    setFlechas((actuales) => {
      const siguientes = new Map(actuales);
      siguientes.set(clave, { puntos: [], etiquetaDx: 0, etiquetaDy: 0, ...actuales.get(clave), ...patch });
      return siguientes;
    });
  }, []);

  const nodoSeleccionado = editando ? nodes.find((n) => n.selected) : null;

  const cambiarForma = (forma) =>
    setNodes((actuales) => actuales.map((n) => (n.id === nodoSeleccionado?.id ? { ...n, data: { ...n.data, forma } } : n)));

  const edges = useMemo(
    () =>
      listaEtapas.flatMap((etapa) =>
        transicionesConIndice(etapa).map(({ transicion, destinoId, indice }) => {
          const clave = claveFlecha(etapa._id, destinoId, indice);
          // Una transición "de regreso" (a la misma etapa o a un nivel anterior) comparte el mismo
          // par izquierda/derecha que la de ida y quedaría dibujada exactamente encima -- se manda por
          // arriba con sus propios conectores para que se vea como una flecha aparte.
          const esRetroceso = esRetrocesoDe(etapa._id, destinoId);
          const flecha = flechas.get(clave) || {};
          const sourceHandle = flecha.sourceHandle || (esRetroceso ? "top-source" : "right-source");
          const targetHandle = flecha.targetHandle || (esRetroceso ? "top-target" : "left-target");
          return {
            id: clave,
            source: etapa._id,
            target: destinoId,
            sourceHandle,
            targetHandle,
            type: "etapa",
            label: transicion.etiqueta || undefined,
            style: { stroke: "#8c8c8c", strokeWidth: 1, ...(esRetroceso && { strokeDasharray: "6 4" }) },
            markerEnd: { type: MarkerType.ArrowClosed, color: "#8c8c8c" },
            data: {
              ...flecha,
              clave,
              esRetroceso: sourceHandle.startsWith("top") && targetHandle.startsWith("top"),
              editando,
              onCambiar: cambiarFlecha,
            },
          };
        })
      ),
    [listaEtapas, flechas, editando, esRetrocesoDe, cambiarFlecha]
  );

  const nodosVisibles = useMemo(() => {
    const enUso = new Map();
    const marcar = (nodoId, conector) => {
      if (!enUso.has(nodoId)) enUso.set(nodoId, new Set());
      enUso.get(nodoId).add(conector);
    };
    edges.forEach((e) => {
      marcar(e.source, e.sourceHandle);
      marcar(e.target, e.targetHandle);
    });
    return nodes.map((n) => ({ ...n, data: { ...n.data, conectoresEnUso: enUso.get(n.id), editando } }));
  }, [nodes, edges, editando]);

  const moverExtremoFlecha = useCallback(
    (flechaAnterior, conexion) => {
      if (conexion.source !== flechaAnterior.source || conexion.target !== flechaAnterior.target) {
        message.warning("Solo puedes mover el extremo de la flecha dentro de la misma etapa.");
        return;
      }
      cambiarFlecha(flechaAnterior.id, { sourceHandle: conexion.sourceHandle, targetHandle: conexion.targetHandle });
    },
    [cambiarFlecha]
  );

  if (listaEtapas.length === 0) {
    return <Empty description="Sin etapas para dibujar" />;
  }

  return (
    <>
      {puedeEditar && (
        <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          {editando ? (
            <>
              <Space>
                {nodoSeleccionado && (
                  <SegmentedItem
                    value={nodoSeleccionado.data.forma}
                    onChange={cambiarForma}
                    options={[
                      { label: "Rectángulo", value: "rectangulo" },
                      { label: "Rombo de decisión", value: "rombo" },
                      { label: "Círculo de inicio", value: "inicio" },
                      { label: "Círculo de fin", value: "fin" },
                    ]}
                  />
                )}
              </Space>
              <Space style={{ marginLeft: "auto" }}>
                <ButtonItem icon={<UndoOutlined />} naranja  onClick={restablecer}>
                  Restablecer
                </ButtonItem>
                <ButtonItem icon={<CloseOutlined />} rojo onClick={cancelar}>
                  Cancelar
                </ButtonItem>
                <ButtonItem type="primary" icon={<SaveOutlined />} loading={guardando} onClick={guardar}>
                  Guardar
                </ButtonItem>
              </Space>
            </>
          ) : (
            <>
              <Typography.Text type="secondary">
                {bloqueoAjeno ? (
                  <>
                    <LockOutlined /> Lo está editando {bloqueoAjeno}
                  </>
                ) : (
                  onAbrirEtapa && "Doble clic en una etapa para editarla"
                )}
              </Typography.Text>
              <ButtonItem style={{ marginLeft: "auto" }} icon={<EditOutlined />} disabled={!!bloqueoAjeno} onClick={empezarEdicion}>
                Editar diagrama
              </ButtonItem>
            </>
          )}
        </div>
      )}
      {editando && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message="Agrega primero todas las etapas y después acomoda el diagrama."
          description="Arrastra las etapas para moverlas y selecciona una para cambiar su forma. Clic sobre una flecha agrega un quiebre: arrástralo para doblarla y dale doble clic para quitarlo. Las etiquetas de las flechas también se arrastran, y el extremo de una flecha se puede llevar a otro punto de la misma etapa."
        />
      )}
      <div style={{ height: 480, border: `1px solid ${editando ? "#91d5ff" : "#f0f0f0"}`, borderRadius: 8 }}>
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodosVisibles}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgeUpdate={editando ? moverExtremoFlecha : undefined}
            edgeUpdaterRadius={12}
            onNodeDoubleClick={(_, nodo) => onAbrirEtapa && onAbrirEtapa(nodo.id)}
            fitView
            nodesDraggable={editando}
            nodesConnectable={editando}
            elementsSelectable={editando}
            zoomOnDoubleClick={false}
          >
            <Background />
            <Controls showInteractive={false} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    </>
  );
};

export default DiagramaEtapasPU;
